import { HttpParams } from '@angular/common/http';

import { Maybe, Nullish, SingleOrArray } from '@plugins/shared/types/helpers';
import { returnAsArray } from '@plugins/shared/utils/arrayUtils/array-utils';
import {
  isNonNullish,
  isNullish,
  isObject,
} from '@plugins/shared/utils/assertFunctions';
import {
  assertedSizeInputParser,
  sizeInputParser,
} from '@plugins/shared/utils/sizeInputParser/sizeInputParser';

import { baseAvatarStyle } from '../abstract-components/atomic/user-avatar.component.base-style';
import { baseUserNameStyle } from '../abstract-components/atomic/user-name.component.base-style';
import {
  AVATAR_BASE_SIZE,
  AVATAR_TO_NAME_RATIO,
  BASE_FONT_SIZE,
  BASE_LINE_HEIGHT_SIZE,
  PICKED_HEIGHT_TO_NAME_RATIO,
} from '../config';

import type {
  AllEntityTypeNames,
  AllEntityTypes,
  ApiDetailsRequestDto,
  ApiRequestQueries,
  ApiSearchRequestDto,
  CalculateComponentSizes,
  DirectRequestType,
  EvaluateSearchControl,
  GeneratedAvatarStyles,
  GeneratedUserNameStyles,
  GenerateDynamicStyle,
  SearchSwitch,
  SearchTypes,
} from './types.private';

export const searchTypeSetter = (toggleStatus: SearchSwitch): SearchTypes => {
  const { user, group } = toggleStatus;
  if (user && group) {
    return 'all';
  }
  if (user && !group) {
    return 'user';
  }
  if (!user && group) {
    return 'group';
  }
  return 'none';
};

export const searchSwitchSetter = (searchType: SearchTypes): SearchSwitch => {
  switch (searchType) {
    case 'all':
      return { user: true, group: true };
    case 'group':
      return { user: false, group: true };
    case 'user':
      return { user: true, group: false };
    case 'none':
      return { user: false, group: false };
    default:
      return { user: true, group: true };
  }
};

const isApiRequestWithIncludedGroups = (
  arg: ApiRequestQueries,
): arg is Required<ApiSearchRequestDto> => {
  return 'includedGroups' in arg && isNonNullish(arg.includedGroups);
};

export const setParams = ({
  query,
  groupType,
}: DirectRequestType): HttpParams => {
  const paramMap: Map<string, string | string[]> = new Map();

  if ('id' in query) {
    if (!query.id.length)
      throw new Error('Empty strings or arrays not allowed!');
    paramMap.set('id', query.id);
  }

  if ('search' in query) {
    if (!query.search.length) throw new Error('Empty strings not allowed!');
    paramMap.set('search', query.search);

    if (groupType !== 'group' && isApiRequestWithIncludedGroups(query)) {
      paramMap.set('includedGroups', query.includedGroups);
    }
  }
  let params = new HttpParams();
  for (const key of paramMap.keys()) {
    const arrParams = returnAsArray(paramMap.get(key)).filter(
      (param): param is string =>
        typeof param === 'string' && param.length !== 0,
    );
    arrParams.forEach((param, index) => {
      if (!index) {
        params = params.set(key, param);
        return;
      }
      params = params.append(key, param);
    });
  }
  return params;
};

export const generateAvatarStyle: GenerateDynamicStyle<
  GeneratedAvatarStyles
> = (sizeArg) => {
  let parsedSize = sizeInputParser(sizeArg);
  if (!parsedSize) {
    parsedSize = assertedSizeInputParser(AVATAR_BASE_SIZE);
  }

  const [width, height] = [0, 1].map(() => parsedSize.composite);
  return { ...baseAvatarStyle, width, height };
};

export const generateUserNameStyle: GenerateDynamicStyle<
  GeneratedUserNameStyles
> = (sizeArg) => {
  const generatedStyle = baseUserNameStyle;
  const parsedSize = sizeInputParser(sizeArg);
  if (!parsedSize) return generatedStyle;
  return {
    fontSize: parsedSize.composite,
  };
};

export const generateListItemHeight = (
  size: string | number,
  multiplier: number,
): string => {
  const parsedSize = sizeInputParser(size);
  if (parsedSize) {
    return `${parsedSize.value * multiplier}${parsedSize.unit}`;
  }
  return BASE_LINE_HEIGHT_SIZE;
};

/**On the basis of passed size prop, return size properties
 * for the UserAvatar and UserName components.
 */
export const calculateComponentSizes: CalculateComponentSizes = (size) => {
  // Parse size for userName component, so that no percentages are allowed here.
  let parsedSize = sizeInputParser(size);
  if (!parsedSize) {
    parsedSize = assertedSizeInputParser(BASE_FONT_SIZE);
  }
  const { value, unit, composite } = parsedSize;
  const avatarSize = Number((value * AVATAR_TO_NAME_RATIO).toFixed(2));
  const listItemHeight = Number(
    (value * PICKED_HEIGHT_TO_NAME_RATIO).toFixed(2),
  );
  return {
    userNameSize: composite,
    userAvatarSize: `${avatarSize}${unit}`,
    pickedListHeight: `${listItemHeight}${unit}`,
  };
};
/** Check and return positive integers for a given input property.
 */
export const pickEntitiesLimitTransform = (
  input?: string | number | Nullish,
): number | null => {
  if (isNullish(input)) return null;
  if (typeof input === 'number') {
    return Math.floor(Math.abs(input));
  }
  const toNumber = Number(input);
  if (Number.isNaN(toNumber)) {
    return null;
  }
  return Math.floor(Math.abs(toNumber));
};

export const isPercentageSize = (size: string | number): boolean => {
  if (typeof size === 'number') return false;
  const cleaned = size.trim().replace(/\s,{2,}/g, ' ');
  const match = cleaned.match(/%$/);
  return match ? true : false;
};

/** For use in composite components, transform percentage sizes to em units and apply multiplier.
 *
 * It covers the cases when a percentage value is passed as the size prop.
 * Passing the percentage values directly would lead to incorrect sizings
 * of the UserAvatarComponent and the height of individual list item.*/
export const generateCompositeSize = (
  input: Maybe<number | string>,
  multiplierValue?: number,
): string | undefined => {
  if (isNullish(input)) return undefined;
  const isPercentage = isPercentageSize(input);
  const initialSize = sizeInputParser(input);
  if (!initialSize) return undefined;
  const [value, unit] = [
    initialSize.value * (isPercentage ? 0.01 : 1) * (multiplierValue ?? 1),
    isPercentage ? 'em' : initialSize.unit,
  ];
  // Round and truncate unnecessary values for non integers.
  let finalValue = value.toString();
  const isInteger = Number.isInteger(value);
  if (!isInteger) {
    finalValue = value.toPrecision(3).replace(/0$/, '');
  }
  return `${finalValue}${unit}`;
};

/** Returns a boolean on whether or not a control should be enabled|disabled
 * with respect to the maximum number of allowed picks.
 */
export const evaluateSearchControl: EvaluateSearchControl = ({
  picked,
  maxAllowed,
  disabled,
}) => {
  if (maxAllowed === null) return false;
  const shouldBeEnabled = picked < maxAllowed;
  if (shouldBeEnabled && disabled) return true;
  if (!shouldBeEnabled && !disabled) return true;
  return false;
};

export const generateIdArray = (ids: Maybe<string | string[]>): string[] => {
  if (isNullish(ids)) return [];
  return [ids].flat().filter((id) => id);
};

/**Check if an entity satisfies basic requirements of AllEntityTypes type
 * Arg must be at least an object type, and contain shared properties of the appropriate type. Can contain additional properties*/
export const checkProvidedEntity = (arg: unknown): arg is AllEntityTypes => {
  if (arg === undefined || arg === null) return false;
  if (typeof arg !== 'object') return false;
  const sharedProperties = [
    'id',
    'name',
    'type',
  ] satisfies (keyof AllEntityTypes)[];
  const propertiesExist = (
    arg: unknown,
  ): arg is Record<(typeof sharedProperties)[number], string> =>
    isObject(arg) &&
    sharedProperties.every(
      (property) => property in arg && typeof property === 'string',
    );
  if (!propertiesExist(arg)) return false;

  const allowedTypeValues: AllEntityTypeNames[] = ['missing', 'user', 'group'];

  const isTypePropProper = allowedTypeValues.some((val) => arg.type === val);

  return isTypePropProper;
};

export const areArraysEqual = <T extends string | number | boolean>(
  a: T[],
  b: T[],
): boolean => {
  if (a.length !== b.length) return false;
  return a.every((entry, index) => b[index] === entry);
};

/** Generating request cache ids.
 *
 * Synchronous and transparent alternative to using digests.  */
export const stringifyRequest = (
  request: ApiDetailsRequestDto | ApiSearchRequestDto,
  searchType: SearchTypes,
): string => {
  const SEARCH_TYPE = 'type',
    ID = 'id',
    SEARCH = 'search',
    INCLUDED_GROUPS = 'includedGroups';

  const joinFunction = (
    parts: Array<[string, SingleOrArray<string>]>,
  ): string => {
    const cacheParts = parts
      .filter(([_key, entry]) => !!entry.length)
      .map(([key, entry]) => `${key}=${returnAsArray(entry).join(',')}`);
    return cacheParts.join(':');
  };
  if ('id' in request) {
    return joinFunction([
      [SEARCH_TYPE, searchType],
      [ID, request.id],
    ]);
  }

  let includedGroups: string[] = returnAsArray(request.includedGroups ?? []);

  // Ignore includedGroups for group types as it this isn't a sensible request.
  if (searchType === 'group') includedGroups = [];

  const cacheParts: [string, SingleOrArray<string>][] = [
    [SEARCH_TYPE, searchType],
    [SEARCH, request.search],
    // we slice the includedGroups array to avoid sort errors for frozen
    // arrays. We sort the array, to avoid different caches for same items
    // with different ordering.
    [INCLUDED_GROUPS, includedGroups.slice().sort()],
  ];
  return joinFunction(cacheParts);
};
